import React, { useState, useEffect } from "react";
import Select from "react-select";

import axiosClient from "../../../../api/axiosClient";

import {
  previewManufacturerMapping,
  applyManufacturerMapping
} from "./bomMappingService";

import {
  getCategoryOptions,
  getManufacturerOptions
} from "./bomMappingMetaService";

// ---------------------------------------------------------
const thStyle = {
  border: "1px solid #ccc",
  padding: "10px",
  textAlign: "left",
  position: "sticky",
  top: 0,
  background: "linear-gradient(90deg, #4b6cb7, #182848)",
  color: "#fff",
  zIndex: 1
};

const tdStyle = {
  border: "1px solid #ccc",
  padding: "8px"
};

// ---------------------------------------------------------
const ManufacturerMappingScreen = ({ enquiryItems = [], bomList = [] }) => {

  const [materialOptions, setMaterialOptions] = useState([]);

  const [filters, setFilters] = useState({
    category: "",
    manufacturer: "",
    bom_ids: []
  });

  const [mappingFilter, setMappingFilter] = useState("ALL");
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [bomOptions, setBomOptions] = useState([]);

  const [manualMappings, setManualMappings] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const selectPortalProps = {
    menuPortalTarget: document.body,
    menuPosition: "fixed",
    styles: {
      menuPortal: base => ({ ...base, zIndex: 9999 })
    }
  };

  const [isFullScreen, setIsFullScreen] = useState(false);

  // ---------------------------------------------------------
  // FULLSCREEN
  // ---------------------------------------------------------
  const enterFullScreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) elem.requestFullscreen();
    setIsFullScreen(true);
  };

  const exitFullScreen = () => {
    if (document.exitFullscreen) document.exitFullscreen();
    setIsFullScreen(false);
  };

  useEffect(() => {
    const handler = () => {
      if (!document.fullscreenElement) setIsFullScreen(false);
    };
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // ---------------------------------------------------------
  useEffect(() => {
    if (bomList && bomList.length > 0) {
      loadDropdowns();
    }
  }, [bomList]);

  const loadDropdowns = async () => {
    try {

      const rmRes = await axiosClient.get("/api/ppe/masters/raw-materials/all");
      const rmData = rmRes?.data?.data || [];

      setMaterialOptions(
        rmData.map(m => ({
          label: m.material_name,
          value: m.id,
          category: m.category
        }))
      );

      const [catRes, mfgRes] = await Promise.all([
        getCategoryOptions(),
        getManufacturerOptions()
      ]);

      const categoryData = catRes?.data?.data?.data || catRes?.data?.data || [];
      const manufacturerData = mfgRes?.data?.data?.data || mfgRes?.data?.data || [];

      const bomData = (bomList || []).map(b => ({
        id: b.id,
        code: b.code || `BOM-${b.id}`
      }));

      setCategoryOptions(categoryData);
      setManufacturerOptions(manufacturerData);
      setBomOptions(bomData);

      if (bomData.length > 0) {
        const defaultFilters = {
          category: "",
          manufacturer: "",
          bom_ids: bomData.map(b => Number(b.id))
        };

        setFilters(defaultFilters);

        setTimeout(() => {
          handlePreview(defaultFilters);
        }, 200);
      }

    } catch (err) {
      console.error("Dropdown load failed", err);
    }
  };

  // ---------------------------------------------------------
  const categorySelectOptions = categoryOptions.map(c => ({
    label: c.label || c.value || c,
    value: c.value || c.label || c || ""
  }));

  const manufacturerSelectOptions = manufacturerOptions.map(m => ({
    label: m.label || m.value || m,
    value: m.value || m.label || m || ""
  }));

  const bomSelectOptions = bomOptions.map(b => ({
    label: b.code,
    value: b.id
  }));

  const mappingFilterOptions = [
    { label: "All", value: "ALL" },
    { label: "Mapped", value: "MAPPED" },
    { label: "Unmapped", value: "UNMAPPED" }
  ];

  // ---------------------------------------------------------
  const handlePreview = async (overrideFilters) => {

    const raw = overrideFilters || filters;

    const f = {
      category: typeof raw.category === "object"
        ? raw.category?.value || ""
        : raw.category,

      manufacturer: typeof raw.manufacturer === "object"
        ? raw.manufacturer?.value || ""
        : raw.manufacturer,

      bom_ids: (raw.bom_ids || []).map(Number)
    };

    if (f.bom_ids.length === 0) return;

    try {
      setLoading(true);

      const res = await previewManufacturerMapping(f);
      const payload = res?.data?.data || res?.data || {};

      setPreviewData(payload);
      setCurrentPage(1);

    } catch (err) {
      alert("Failed to fetch preview");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------
  const handleApply = async () => {

    if (!previewData) {
      alert("Please run preview first");
      return;
    }

    if (!window.confirm("Apply mapping?")) return;

    try {
      setLoading(true);

      const payload = {
        bom_ids: filters.bom_ids.map(Number),
        manual_mappings: manualMappings
      };

      if (filters.category) payload.category = filters.category;
      if (filters.manufacturer) payload.manufacturer = filters.manufacturer;

      await applyManufacturerMapping(payload);

      handlePreview(filters);

    } catch (err) {
      alert("Failed to apply mapping");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------
  let itemsList = Array.isArray(previewData?.items)
    ? previewData.items
    : [];

  if (mappingFilter === "MAPPED") {
    itemsList = itemsList.filter(i => i.mapped_material_id);
  }

  if (mappingFilter === "UNMAPPED") {
    itemsList = itemsList.filter(i => !i.mapped_material_id);
  }

  const getStatus = (row) =>
    row.status || (row.mapped_material_id ? "MAPPED" : "UNMAPPED");

  const getRowColor = (status) =>
    status === "MAPPED" ? "#e6ffed" : "#ffe6e6";

  const getType = (row) =>
    row.is_auto_generated ? "DERIVED" : "BASE";

  const getFilteredOptions = (row) => {

    if (!row.category) return materialOptions;

    const filtered = materialOptions.filter(m =>
      m.category?.toLowerCase().trim() ===
      row.category?.toLowerCase().trim()
    );

    return filtered.length ? filtered : materialOptions;
  };

  // ---------------------------------------------------------
  return (
    <div style={{
      padding: "20px",
      background: "#fff",
      height: isFullScreen ? "100vh" : "auto",
      overflow: "auto"
    }}>

      <h2>BOM → Manufacturer Mapping</h2>

      {/* FILTER BAR */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "250px 250px 350px 200px auto auto auto",
        gap: "10px",
        marginBottom: "20px"
      }}>

        <Select {...selectPortalProps}
          options={categorySelectOptions}
          placeholder="Category"
          value={categorySelectOptions.find(o => o.value === filters.category)}
          onChange={(s) => setFilters(p => ({ ...p, category: s?.value || "" }))}
        />

        <Select {...selectPortalProps}
          options={manufacturerSelectOptions}
          placeholder="Manufacturer"
          value={manufacturerSelectOptions.find(o => o.value === filters.manufacturer)}
          onChange={(s) => setFilters(p => ({ ...p, manufacturer: s?.value || "" }))}
        />

        <Select {...selectPortalProps}
          isMulti
          options={bomSelectOptions}
          placeholder="BOM(s)"
          value={bomSelectOptions.filter(o => filters.bom_ids.includes(o.value))}
          onChange={(s) => setFilters(p => ({
            ...p,
            bom_ids: s?.map(x => x.value) || []
          }))}
        />

        <Select {...selectPortalProps}
          options={mappingFilterOptions}
          value={mappingFilterOptions.find(o => o.value === mappingFilter)}
          onChange={(s) => setMappingFilter(s.value)}
        />

        <button style={{ background: "#4CAF50", color: "#fff" }}
          onClick={() => handlePreview(filters)}>
          Preview
        </button>

        <button style={{ background: "#2196F3", color: "#fff" }}
          onClick={handleApply}>
          Apply
        </button>

        <button style={{ background: "#555", color: "#fff" }}
          onClick={isFullScreen ? exitFullScreen : enterFullScreen}>
          {isFullScreen ? "Restore" : "Full Screen"}
        </button>
      </div>

      {/* TABLE */}
      {itemsList.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={thStyle}>#</th>
              <th style={thStyle}>BOM</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Original SKU</th>
              <th style={thStyle}>Mapped SKU</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Manual Override</th>
            </tr>
          </thead>

          <tbody>
            {itemsList.map((row, i) => {
              const key = `${row.bom_id}_${row.original_material_id}`;
              const status = getStatus(row);

              return (
                <tr key={key} style={{ background: getRowColor(status) }}>
                  <td style={tdStyle}>{i + 1}</td>
                  <td style={tdStyle}>{row.bom_id}</td>
                  <td style={tdStyle}>{getType(row)}</td>
                  <td style={tdStyle}>{row.original_material_name}</td>
                  <td style={tdStyle}>{row.mapped_material_name || "-"}</td>
                  <td style={tdStyle}><b>{status}</b></td>

                  <td style={tdStyle}>
                    <Select
                      {...selectPortalProps}
                      options={
                        row.possible_mfg_skus?.length
                          ? row.possible_mfg_skus.map(m => ({
                              label: m.name,
                              value: m.id
                            }))
                          : getFilteredOptions(row)
                      }
                      value={
                          (row.possible_mfg_skus || []).map(m => ({
                            label: m.name,
                            value: m.id
                          })).find(o => o.value === (manualMappings[key] || row.mapped_material_id))
                        }
                      onChange={(s) =>
                        setManualMappings(prev => ({
                          ...prev,
                          [key]: s?.value || ""
                        }))
                      }
                      placeholder="Override"
                      isClearable
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {!loading && itemsList.length === 0 && (
        <div style={{ marginTop: "20px", color: "#888" }}>
          No data found
        </div>
      )}
    </div>
  );
};

export default ManufacturerMappingScreen;